import React, { useEffect } from 'react';
import moment from 'moment';
import { Box, Button } from '@mui/material';
import { useForm, useWatch } from 'react-hook-form';
import { DateRangeWithPeriods, SelectField } from 'components/form';
import { convertEnumToSelectOptions } from '../../../utils/selectOptionsHelper';
import { ChangelogFiltersInput, ChangelogFiltersFormValues, EnumLike } from './types';

const buildFiltersInput = (filters: ChangelogFiltersFormValues): ChangelogFiltersInput | undefined => {
  const result: ChangelogFiltersInput = {};

  if (filters.changeType && filters.changeType !== 'all') {
    result.changeTypes = [filters.changeType];
  }

  result.dateRange = {
    from: filters.dateFrom.toISOString(),
    to: filters.dateTo.endOf('day').toISOString(),
  };

  return Object.keys(result).length ? result : undefined;
};

interface Props<Events extends EnumLike> {
  loading: boolean;
  events: Events;
  onFiltersChanged: (filters: ChangelogFiltersInput | undefined) => void;
}

export const ChangelogFilters = <Events extends EnumLike>({ loading, onFiltersChanged, events }: Props<Events>) => {
  const form = useForm<ChangelogFiltersFormValues>({
    defaultValues: {
      changeType: 'all',
      dateFrom: moment().startOf('year'),
      dateTo: moment().endOf('year'),
    },
  });

  const filters = useWatch({ control: form.control }) as ChangelogFiltersFormValues;

  useEffect(() => {
    onFiltersChanged(buildFiltersInput(filters));
  }, [filters, onFiltersChanged]);

  const handleReset = () => {
    form.reset();
  };

  const handleRangeChange = async (range: { endDate: moment.Moment; startDate: moment.Moment }) => {
    form.setValue('dateFrom', range.startDate);
    form.setValue('dateTo', range.endDate);
  };

  return (
    <Box sx={{ display: 'grid', gridGap: '1rem', gridTemplateColumns: '2fr 1fr 8rem', alignItems: 'flex-start' }}>
      <DateRangeWithPeriods
        size="small"
        nameTo="dateTo"
        variant="outlined"
        nameFrom="dateFrom"
        control={form.control}
        onRangeChange={handleRangeChange}
      />

      <SelectField
        name="changeType"
        label="Change Type"
        variant="outlined"
        control={form.control}
        options={[{ label: 'All', value: 'all' }, ...convertEnumToSelectOptions<string, Events>(events)]}
      />

      <Button disabled={loading} variant="contained" onClick={handleReset}>
        Reset
      </Button>
    </Box>
  );
};
